/**
 * What the second pass is examining, narrated from the field constraints the
 * request actually carried.
 *
 * The wait is one opaque model call, so there is no real sequence to report.
 * What there is, honestly, is scope: the constraints collected from this form
 * describe the ground the pass has to cover, and they differ between forms and
 * companies. So the panel names subjects rather than steps — present tense,
 * no completion claimed, and no second lap through the list, which is what
 * would give away that the narration was never tied to anything.
 *
 * A topic appears only when a constraint in this run matches it. A lease with
 * no percentage-rent fields never says it is checking percentage rent.
 */

// Seconds a topic holds before the next one. Roughly two minutes of narration
// across a full list, which is the shape of the second pass; a run that
// outlives the list stops narrating rather than looping.
export const SECONDS_PER_TOPIC = 6;

// Ordered as a reader meets them in a lease, so stepping through reads as a
// walk down the document. Prefixes are matched against the first segment of a
// constraint's category — `baseRent[0].amount` and `baseRentType` both answer
// to `baseRent` — and the first matching topic claims the constraint, so a
// narrow group must precede the broad one it would otherwise fall into
// (`insuranceReimbursement` is an operating expense, not an insurance
// requirement).
export const NARRATION_TOPICS = Object.freeze([
  {
    label: 'the parties and notice addresses',
    prefixes: [
      'tenantInfo',
      'landlordInfo',
      'guarantorInfo',
      'existingGuarantorInfo',
      'franchisorInfo',
      'tenantRep',
      'landlordRep',
      'tenantAttorney',
      'landlordAttorney',
      'lenderInfo',
      'escrowAgentInfo',
    ],
  },
  {
    label: 'the recitals and defined terms',
    prefixes: [
      'originalLease',
      'previousAmendment',
      'priorDocuments',
      'definedTerms',
    ],
  },
  {
    label: 'the premises and rentable area',
    prefixes: [
      'premises',
      'premiseSuites',
      'temporaryPremises',
      'landlordAreaRemeasurements',
      'holdSpace',
    ],
  },
  {
    label: 'the permitted use and exclusives',
    prefixes: [
      'permittedUse',
      'permittedUseOptions',
      'exclusiveUse',
      'tenantCompetitors',
      'materialInterferenceRadius',
    ],
  },
  {
    label: 'the term and commencement dates',
    prefixes: [
      'leaseYears',
      'leaseMonths',
      'termNumber',
      'termCommencementDate',
      'commencementDate',
      'estimatedCommencementDate',
      'expirationDate',
      'naturalExpirationDate',
    ],
  },
  {
    label: 'delivery and possession',
    prefixes: [
      'estimatedDeliveryDate',
      'hardEstimatedDeliveryDate',
      'softEstimatedDeliveryDate',
      'landlordLateDeliveryPenalty',
      'landlordDelayAmount',
      'landlordDelayAbatementTrigger',
      'requiredOpening',
      'permitContingency',
      'tenantPermitContingency',
    ],
  },
  {
    label: 'the base rent schedule',
    prefixes: [
      'baseRent',
      'annualIncrease',
      'rentPeriods',
      'rentInputType',
      'rentBumps',
      'flexibleRentTable',
    ],
  },
  {
    label: 'rent abatement and free rent',
    prefixes: [
      'hasFreeRent',
      'freeRent',
      'freeRentPeriods',
      'rentDeferral',
      'rentReduction',
      'reductionPeriods',
    ],
  },
  {
    label: 'percentage rent and gross sales reporting',
    prefixes: [
      'hasPercentageRent',
      'percentageRent',
      'pctRent',
      'existingPercentageRentRate',
      'grossSalesReporting',
      'grossSalesReductionNotice',
      'minimumSales',
      'salesTestPeriod',
    ],
  },
  {
    label: 'additional rent',
    prefixes: [
      'additionalRent',
      'additionalRentType',
      'hasPromotionFund',
      'promotionFund',
    ],
  },
  {
    label: 'operating expenses and reimbursements',
    prefixes: [
      'commonAreaReimbursement',
      'insuranceReimbursement',
      'realEstateReimbursement',
      'taxBaseYear',
      'expenseBaseYear',
      'officeMaintenanceBaseYear',
      'proportionateShare',
      'proporShareBreakdown',
      'opEx',
      'opExExclusions',
      'fixedCam',
      'camCap',
      'capOnCamFirstYear',
      'capExCompromise',
      'hasManagementFee',
      'managementFee',
    ],
  },
  {
    label: "the improvement allowance and landlord's work",
    prefixes: [
      'tiAllowance',
      'hasTiAllowance',
      'tenantImprovement',
      'tenantImprovements',
      'premisesImprovements',
      'spacePlanningAllowance',
      'fitTestAllowanceAmount',
      'whoCompletesPlans',
      'tenantPlanSubmissionDate',
      'landlordArchitect',
      'landlordWorkDeliveryCondition',
      'landlordWarranty',
      'fixturing',
      'hasLandlordDoesWorkAfterOccupancy',
    ],
  },
  {
    label: 'the security deposit and guaranty',
    prefixes: [
      'security',
      'securityDeposit',
      'hasSecurityDeposit',
      'securityDepositUpdate',
      'guaranty',
    ],
  },
  {
    label: 'the insurance requirements',
    prefixes: ['insurance'],
  },
  {
    label: 'the renewal options',
    prefixes: ['hasRenewalOption', 'renewal', 'terms'],
  },
  {
    label: 'expansion and first-refusal rights',
    prefixes: [
      'expansion',
      'expansionOption',
      'expansionNotice',
      'contractionOption',
      'reduction',
      'rightOfFirstOffer',
      'rightOfFirstRefusal',
    ],
  },
  {
    label: 'the termination rights',
    prefixes: [
      'tenantTerminationOption',
      'hardTerminationDate',
      'landlordPctRentTermination',
    ],
  },
  {
    label: 'the co-tenancy provisions',
    prefixes: ['ongoingCoTenancy', 'openingCoTenancy'],
  },
  {
    label: 'assignment and subletting',
    prefixes: ['assigneeInfo', 'priorAssignments', 'releaseAssignor'],
  },
  {
    label: 'the parking rights',
    prefixes: [
      'hasParking',
      'parking',
      'reservedParking',
      'unreservedParking',
      'exclusiveParking',
    ],
  },
  {
    label: 'signage and rooftop rights',
    prefixes: ['signage', 'monumentSign', 'rooftop'],
  },
  {
    label: "landlord's relocation right",
    prefixes: ['relocation'],
  },
  {
    label: 'the holdover provisions',
    prefixes: ['holdover', 'scaledHoldover', 'hasTenancyAtSufferanceChange'],
  },
]);

// The first identifier in a constraint's category. `category` is the path
// without its `lease.` prefix, so `baseRent[0].amount` and `tenantInfo.name`
// both reduce to the group they belong to.
function headSegment(constraint) {
  const path = String(
    (constraint && (constraint.category || constraint.path)) || '',
  ).replace(/^lease\./, '');

  return path.split(/[.[]/)[0];
}

/**
 * The topics named by the concept list phase two was given, in the order the
 * source document states them.
 *
 * Better than the constraint table below on every count where they differ: it
 * is what this document actually negotiates rather than what the form could
 * hold, it is what the readers were actually sent, and it needs no mapping
 * table to stay true. The table is the fallback for a run whose concept call
 * failed.
 */
export function narrationTopicsForConcepts(concepts) {
  if (!Array.isArray(concepts)) return [];

  const seen = new Set();
  return concepts
    .map(concept => String((concept && concept.name) || '').trim())
    .filter(name => {
      if (!name) return false;
      const key = name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/**
 * The topics this run can honestly narrate, in reading order.
 *
 * Constraints the table does not recognise contribute nothing rather than
 * falling into a catch-all: a topic no field backs is exactly the invented
 * step this is meant to avoid.
 */
export function narrationTopicsFor(fieldConstraints) {
  if (!Array.isArray(fieldConstraints) || fieldConstraints.length === 0) {
    return [];
  }

  const heads = new Set();
  fieldConstraints.forEach(constraint => {
    const head = headSegment(constraint);
    if (head) heads.add(head);
  });

  const claimed = new Set();
  const labels = [];

  NARRATION_TOPICS.forEach(topic => {
    const matched = [...heads].some(head => {
      if (claimed.has(head)) return false;
      return topic.prefixes.some(prefix => head.indexOf(prefix) === 0);
    });
    if (!matched) return;

    // Claim every head this topic answers for, so a later, broader topic
    // cannot be raised by a field the narrower one already described.
    heads.forEach(head => {
      if (topic.prefixes.some(prefix => head.indexOf(prefix) === 0)) {
        claimed.add(head);
      }
    });
    labels.push(topic.label);
  });

  return labels;
}

/**
 * The topic to show `seconds` into the reading stage, or null once the list is
 * spent — the caller falls back to the general sentence rather than starting
 * the list again.
 */
export function topicAtSecond(topics, seconds) {
  if (!Array.isArray(topics) || topics.length === 0) return null;
  if (!(seconds >= 0)) return topics[0];

  const slot = Math.floor(seconds / SECONDS_PER_TOPIC);
  return slot < topics.length ? topics[slot] : null;
}
